import React from 'react';
import { motion } from 'framer-motion';
import { Lang, ServiceItem } from '../types';

interface ServicePageProps {
  service: ServiceItem;
  lang: Lang;
  onClose: () => void;
}

const ServicePage: React.FC<ServicePageProps> = ({ service, lang, onClose }) => {
  const page = service.pageContent[lang];

  return (
    <motion.div
      initial={{ opacity: 0, y: '100%' }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: '100%' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#0D0D0D',
        zIndex: 200,
        overflowY: 'auto',
      }}
    >
      {/* Hero image */}
      <div style={{ height: '50vh', position: 'relative', overflow: 'hidden' }}>
        <img
          src={service.image}
          alt={service.title[lang]}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(13,13,13,0.4), rgba(13,13,13,0.88))',
        }} />

        {/* KNX grid overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'linear-gradient(rgba(200,169,110,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(200,169,110,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        {/* Close button */}
        <motion.button
          onClick={onClose}
          whileHover={{ borderColor: '#C8A96E', color: '#C8A96E' }}
          style={{
            position: 'absolute', top: '24px', left: '24px',
            background: 'rgba(13,13,13,0.6)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#FFFFFF',
            padding: '10px 20px',
            fontFamily: 'var(--font-body)',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '2px',
            cursor: 'pointer',
            textTransform: 'uppercase',
            display: 'flex', alignItems: 'center', gap: '8px',
            transition: 'border-color 0.3s, color 0.3s',
          }}
        >
          ← {lang === 'es' ? 'Volver' : lang === 'en' ? 'Back' : 'Retour'}
        </motion.button>

        {/* Title overlay */}
        <div style={{
          position: 'absolute', bottom: '40px',
          left: '0', right: '0', padding: '0 40px',
        }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <span style={{
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '5px',
              textTransform: 'uppercase',
              color: '#C8A96E',
              display: 'block',
              marginBottom: '12px',
            }}>
              {lang === 'es' ? 'Servicio' : lang === 'en' ? 'Service' : 'Service'}
            </span>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(28px, 5vw, 56px)',
              fontWeight: 700,
              color: '#FFFFFF',
              letterSpacing: '-0.5px',
              lineHeight: 1.1,
            }}>
              {page.heading}
            </h1>
          </motion.div>
        </div>

        {/* Gold bottom bar */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #C8A96E, #E8C98A)',
        }} />
      </div>

      {/* Content */}
      <div className="container" style={{ padding: 'clamp(60px, 8vw, 80px) clamp(24px, 7vw, 120px)' }}>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'clamp(16px, 1.4vw, 20px)',
            color: '#AAAAAA',
            lineHeight: 1.8,
            maxWidth: '720px',
            marginBottom: '80px',
            borderLeft: '3px solid #C8A96E',
            paddingLeft: '24px',
          }}
        >
          {page.intro}
        </motion.p>

        {/* Blocks grid */}
        {page.blocks.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2px',
          }}>
            {page.blocks.map((block, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                style={{
                  background: i % 2 === 0 ? '#2D2D2D' : '#1A1A1A',
                  padding: 'clamp(32px, 3vw, 48px) clamp(28px, 2.5vw, 40px)',
                }}
              >
                <div style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '3px',
                  color: '#C8A96E',
                  marginBottom: '16px',
                }}>
                  0{i + 1}
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(16px, 1.4vw, 20px)',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  marginBottom: '16px',
                  lineHeight: 1.3,
                }}>
                  {block.title}
                </h3>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '15px',
                  lineHeight: 1.8,
                  color: '#AAAAAA',
                }}>
                  {block.text}
                </p>
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          style={{ textAlign: 'center', marginTop: '80px' }}
        >
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '18px',
            color: '#555555',
            marginBottom: '32px',
          }}>
            {lang === 'es' ? '¿Te interesa este servicio?' :
             lang === 'en' ? 'Interested in this service?' :
             'Ce service vous intéresse ?'}
          </p>
          <motion.a
            href="#contact"
            onClick={onClose}
            whileHover={{ background: '#C8A96E', color: '#0D0D0D' }}
            style={{
              padding: '16px 48px',
              border: '1px solid #C8A96E',
              background: 'transparent',
              color: '#C8A96E',
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'inline-block',
              transition: 'all 0.3s',
            }}
          >
            {lang === 'es' ? 'Solicitar presupuesto' :
             lang === 'en' ? 'Request a quote' :
             'Demander un devis'}
          </motion.a>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ServicePage;
